/**
 * useTimelineDomain Hook
 *
 * Computes the min/max date bounds from all timeline segments.
 * Handles edge cases like null dates (ongoing periods) and provides padding.
 */

import { useMemo } from 'react'
import type { TimelineSegment, TimelineDomain } from '../types'

interface UseTimelineDomainOptions {
  /** Segments to compute domain from */
  segments: TimelineSegment[]
  /** Additional segments (e.g., background history) */
  additionalSegments?: TimelineSegment[]
  /** Padding factor (0-1). Default: 0.15 (15% on each side) */
  paddingFactor?: number
  /** Minimum padding in days. Default: 30 */
  minPaddingDays?: number
  /** Whether to include "today" in domain calculation. Default: true */
  includeToday?: boolean
}

/**
 * Computes timeline domain bounds from segments
 */
export function useTimelineDomain({
  segments,
  additionalSegments = [],
  paddingFactor = 0.15,
  minPaddingDays = 30,
  includeToday = true,
}: UseTimelineDomainOptions): TimelineDomain {
  return useMemo(() => {
    const allSegments = [...segments, ...additionalSegments]

    if (allSegments.length === 0) {
      // No segments: default to 60 days around today
      const now = Date.now()
      const thirtyDays = 30 * 24 * 60 * 60 * 1000
      return {
        minDate: now - thirtyDays,
        maxDate: now + thirtyDays,
        span: thirtyDays * 2,
        padding: 7 * 24 * 60 * 60 * 1000,
      }
    }

    // Collect all dates from segments
    const dates: number[] = []

    for (const segment of allSegments) {
      if (segment.effectiveFrom) {
        dates.push(new Date(segment.effectiveFrom).getTime())
      }
      if (segment.effectiveTo) {
        dates.push(new Date(segment.effectiveTo).getTime())
      }
    }

    // Include today if requested
    if (includeToday) {
      dates.push(Date.now())
    }

    if (dates.length === 0) {
      // All segments have null dates: default to 60 days around today
      const now = Date.now()
      const thirtyDays = 30 * 24 * 60 * 60 * 1000
      return {
        minDate: now - thirtyDays,
        maxDate: now + thirtyDays,
        span: thirtyDays * 2,
        padding: 7 * 24 * 60 * 60 * 1000,
      }
    }

    const rawMinDate = Math.min(...dates)
    const rawMaxDate = Math.max(...dates)
    const rawSpan = rawMaxDate - rawMinDate

    // Calculate padding
    const minPaddingMs = minPaddingDays * 24 * 60 * 60 * 1000
    const calculatedPadding = Math.max(rawSpan * paddingFactor, minPaddingMs)

    // Apply symmetric padding on both sides
    const leftPadding = calculatedPadding
    const rightPadding = calculatedPadding

    const minDate = rawMinDate - leftPadding
    const maxDate = rawMaxDate + rightPadding
    const span = maxDate - minDate

    return {
      minDate,
      maxDate,
      span,
      padding: calculatedPadding,
    }
  }, [segments, additionalSegments, paddingFactor, minPaddingDays, includeToday])
}

/**
 * Merge multiple domains into one that encompasses all of them
 */
export function mergeDomains(...domains: TimelineDomain[]): TimelineDomain {
  if (domains.length === 0) {
    const now = Date.now()
    const thirtyDays = 30 * 24 * 60 * 60 * 1000
    return {
      minDate: now - thirtyDays,
      maxDate: now + thirtyDays,
      span: thirtyDays * 2,
      padding: 7 * 24 * 60 * 60 * 1000,
    }
  }

  const minDate = Math.min(...domains.map((d) => d.minDate))
  const maxDate = Math.max(...domains.map((d) => d.maxDate))
  const padding = Math.max(...domains.map((d) => d.padding))

  return {
    minDate,
    maxDate,
    span: maxDate - minDate,
    padding,
  }
}
